import React, { useMemo, useState } from 'react';
import { Dropdown, MenuProps } from 'antd';
import classNames from 'classnames';
import { useIntl } from 'umi';
import ContactAvatar from '@/components/Contacts/ContactAvatar';
import { ContactModel } from '@/typings/models/Contact';
import { getContactShortName, CONTACT_ENTITY } from '@/utils/contacts';
import { getIntlTranslation, INTL_ACTION } from '@/utils/intl';
import { useLongPress } from '@/hooks/useLongPress';
import styles from './index.module.less';

export type ContactTagActions = {
  onSetPrimary?: (contact: ContactModel) => void;
  onOpen?: (contact: ContactModel) => void;
  onRemove?: (contact: ContactModel) => void;
};

type ContactTagProps = ContactTagActions & {
  contact: Partial<ContactModel>;
  selected?: boolean;
  /** The project's primary contact. */
  primary?: boolean;
  /** Not on the project yet (being added): dashed outline. */
  pending?: boolean;
  /** A contact still being created: grey "?" avatar. */
  placeholder?: boolean;
  /** Overrides the role line (e.g. the role being typed). */
  roleLabel?: string | null;
  onClick?: () => void;
};

const ContactTag: React.FC<ContactTagProps> = ({
  contact,
  selected,
  primary,
  pending,
  placeholder,
  roleLabel,
  onClick,
  onSetPrimary,
  onOpen,
  onRemove,
}) => {
  const intl = useIntl();
  const [menuOpen, setMenuOpen] = useState(false);
  const longPress = useLongPress(() => setMenuOpen(true));
  const saved = contact as ContactModel;

  const menuItems = useMemo<MenuProps['items']>(() => {
    if (pending || placeholder) return [];
    const items: MenuProps['items'] = [];
    if (onSetPrimary && !primary) {
      items.push({
        key: 'primary',
        label: intl.formatMessage({
          id: 'component.contacts.setPrimary',
          defaultMessage: 'Set as primary contact',
        }),
        onClick: () => onSetPrimary(saved),
      });
    }
    if (onOpen) {
      items.push({
        key: 'open',
        label: getIntlTranslation(intl, { entity: CONTACT_ENTITY, action: INTL_ACTION.VIEW }),
        onClick: () => onOpen(saved),
      });
    }
    if (onRemove) {
      // Removing unlinks from the customer, which refuses to lose its primary contact.
      const customerPrimary = Boolean(saved.is_primary);
      if (items.length) items.push({ type: 'divider' });
      items.push({
        key: 'remove',
        danger: true,
        disabled: customerPrimary,
        title: customerPrimary
          ? intl.formatMessage({
              id: 'component.contacts.removePrimaryHint',
              defaultMessage: "Make another contact the customer's primary contact before removing this one.",
            })
          : undefined,
        label: intl.formatMessage({
          id: 'component.contacts.removeFromCustomer',
          defaultMessage: 'Remove from customer',
        }),
        onClick: () => onRemove(saved),
      });
    }
    return items;
  }, [intl, onOpen, onRemove, onSetPrimary, pending, placeholder, primary, saved]);

  const role = roleLabel !== undefined ? roleLabel : contact.role;
  const primaryLabel = intl.formatMessage({
    id: 'component.contacts.primary',
    defaultMessage: 'Primary contact',
  });
  const name =
    getContactShortName(contact) ||
    (placeholder ? getIntlTranslation(intl, { entity: CONTACT_ENTITY, action: INTL_ACTION.NEW }) : '');

  const tag = (
    <button
      type="button"
      className={classNames(styles.tag, {
        [styles.tagSelected]: selected,
        [styles.tagPending]: pending || placeholder,
        [styles.tagMenuOpen]: menuOpen,
      })}
      aria-pressed={selected}
      onClick={onClick}
      {...(menuItems?.length ? longPress : {})}
    >
      <ContactAvatar contact={contact} size="small" placeholder={placeholder} />
      <span className={styles.tagText}>
        <span className={styles.tagName}>{name}</span>
        {(primary || role) && (
          <span className={classNames(styles.tagRole, { [styles.tagRolePrimary]: primary })}>
            {primary ? `★ ${role || primaryLabel}` : role}
          </span>
        )}
      </span>
    </button>
  );

  if (!menuItems?.length) return tag;

  return (
    <Dropdown
      menu={{ items: menuItems, onClick: () => setMenuOpen(false) }}
      trigger={['contextMenu']}
      open={menuOpen}
      onOpenChange={setMenuOpen}
    >
      {tag}
    </Dropdown>
  );
};

export default React.memo(ContactTag);
